import api from './api'; // Assuming you have a configured axios instance

// Define the structure of a Forum Category
export interface ForumCategory {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  // Add other fields if needed, e.g., topicCount
}

/**
 * Fetches all forum categories from the backend.
 */
export const fetchCategories = async (): Promise<ForumCategory[]> => {
  try {
    const response = await api.get<{ success: boolean; data: ForumCategory[] }>('/forum/categories');
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error('API call succeeded but returned no category data.');
  } catch (error: any) {
    console.error("Error fetching forum categories:", error);
    // Rethrow a more specific error or a generic one
    throw new Error(error.response?.data?.message || 'Could not fetch forum categories from API.');
  }
};

// Interface for a single Forum Topic
// Matches the structure returned by the backend's getTopics function
export interface ForumTopic {
  id: number;
  title: string;
  content: string; // Or maybe just a snippet?
  createdAt: string; // ISO date string
  updatedAt: string;
  lastReplyAt: string | null;
  isPinned: boolean;
  isLocked: boolean;
  viewCount: number;
  // Included objects
  author: { id: number; name: string };
  category: { id: number; name: string; slug: string };
  // Counts
  replyCount: number;
  // _count is removed in backend service
}

// Interface for the paginated response from the /topics endpoint
export interface PaginatedTopicsResponse {
  items: ForumTopic[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Fetches forum topics, optionally filtered by category and paginated.
 */
export const fetchTopics = async (
    categoryId?: number | null, // Allow null or undefined for "All Posts"
    page: number = 1, 
    limit: number = 15
): Promise<PaginatedTopicsResponse> => {
  try {
    const params: { categoryId?: number; page: number; limit: number } = {
      page,
      limit,
    };
    if (categoryId !== null && categoryId !== undefined) {
      params.categoryId = categoryId;
    }

    const response = await api.get<{ success: boolean; data: PaginatedTopicsResponse }>('/forum/topics', {
      params: params
    });

    if (response.data.success) {
      return response.data.data;
    }
    throw new Error('API call succeeded but returned no topic data.');
  } catch (error: any) {
    console.error("Error fetching forum topics:", error);
    throw new Error(error.response?.data?.message || 'Could not fetch forum topics from API.');
  }
};

// Interface for the data needed to create a topic
export interface CreateTopicPayload {
  title: string;
  content: string;
  categoryId: number;
}

/**
 * Creates a new forum topic.
 */
export const createTopic = async (payload: CreateTopicPayload): Promise<ForumTopic> => {
  try {
    // Define a type that covers both success and error API responses
    type CreateTopicApiResponse = 
      | { success: true; data: ForumTopic } 
      | { success: false; message: string; error?: any }; // Include optional error details

    // The backend route expects the data in the request body
    const response = await api.post<CreateTopicApiResponse>('/forum/topics', payload);

    if (response.data.success) {
      return response.data.data; // Return the newly created topic object
    }
    // If success is false, the backend should have provided a message
    throw new Error(response.data.message || 'API call failed to create topic.'); // Now message is recognized
  } catch (error: any) {
    console.error("Error creating forum topic:", error);
    // Rethrow a specific error message from the backend if available, or a generic one
    // The error.response?.data might already fit the { success: false, message: ... } structure
    const errorMessage = error.response?.data?.message || 'Could not create forum topic via API.';
    throw new Error(errorMessage);
  }
};

// Add other forum service functions here later (createTopic, etc.) 